const SAFE_EVENT = /^[a-z][a-z0-9_]{2,79}$/;
const SAFE_NAME = /^[a-z][a-z0-9_]{1,63}$/i;
const SAFE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SAFE_DATABASE_CODES = new Set([
  '22023',
  '22p02',
  '23503',
  '23505',
  '23514',
  '40001',
  '42501',
  '42703',
  '42883',
  '42p01',
  '54000',
  '55000',
  '57014',
  'p0001',
  'p0002',
]);

const ALLOWED_DETAIL_KEYS = new Set([
  'code',
  'stage',
  'status',
  'operation',
  'lane',
  'request_id',
  'retryable',
  'count',
  'duration_ms',
]);

const SAFE_STATUS = new Set([
  'starting', 'ready', 'completed', 'failed', 'degraded', 'unavailable',
  'cancelled', 'queued', 'retry_scheduled', 'idle', 'unknown',
]);

function boundedToken(value, fallback = 'unknown') {
  const token = String(value ?? '').trim();
  return SAFE_NAME.test(token) ? token.toLowerCase() : fallback;
}

function boundedCode(value, fallback = 'operation_failed') {
  const code = String(value ?? '').trim().toLowerCase();
  if (SAFE_DATABASE_CODES.has(code)
      || /^pgrst[0-9]{3}$/.test(code)
      || /^http_[1-5][0-9]{2}$/.test(code)
      || /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(code)) return code;
  const safeFallback = String(fallback || '').trim().toLowerCase();
  return /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(safeFallback) ? safeFallback : 'operation_failed';
}

export function safeDiagnosticCode(error, fallback = 'operation_failed') {
  const candidate = error?.diagnosticCode
    || error?.code
    || (Number.isFinite(Number(error?.status)) ? `http_${Number(error.status)}` : '')
    || fallback;
  return boundedCode(candidate, fallback);
}

export function safeDiagnosticDetails(details = {}, error = null) {
  const source = details && typeof details === 'object' && !Array.isArray(details) ? details : {};
  const result = {};
  for (const [key, value] of Object.entries(source)) {
    if (!ALLOWED_DETAIL_KEYS.has(key) || value === undefined || value === null || value === '') continue;
    if (key === 'retryable') result[key] = value === true;
    else if (key === 'count' || key === 'duration_ms') {
      const number = Number(value);
      if (Number.isFinite(number) && number >= 0) result[key] = Math.trunc(number);
    } else if (key === 'status') {
      const status = String(value).trim().toLowerCase();
      result[key] = SAFE_STATUS.has(status) ? status : 'unknown';
    } else if (key === 'request_id') {
      if (SAFE_UUID.test(String(value))) result[key] = String(value).toLowerCase();
    } else result[key] = boundedToken(value);
  }
  result.code = safeDiagnosticCode(error || { code:result.code }, result.code || 'operation_failed');
  return result;
}

// Diagnostics deliberately accept an Error only to derive a bounded code. Error
// messages, stacks, database hints/details, request bodies, and identifiers never
// cross this boundary.
export function reportSafeDiagnostic(level, event, details = {}, error = null, logger = console) {
  const safeEvent = SAFE_EVENT.test(String(event || '')) ? String(event) : 'application_diagnostic';
  const payload = { event:safeEvent, ...safeDiagnosticDetails(details, error) };
  const method = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'info';
  const sink = typeof logger?.[method] === 'function' ? logger[method].bind(logger) : logger?.log?.bind(logger);
  sink?.(JSON.stringify(payload));
  return payload;
}

export function safeUserErrorMessage(error, fallback = 'This operation could not be completed. Retry or contact support if it continues.') {
  const code = safeDiagnosticCode(error);
  if (code === '42501' || code === '403' || code === 'http_403') {
    return 'You do not have permission to complete this operation.';
  }
  if (code === '401' || code === 'http_401') return 'Your session could not be verified. Sign in and try again.';
  if (['pgrst202', 'pgrst204', 'pgrst205', '42883', '42p01', '42703'].includes(code)) {
    return 'This operation is temporarily unavailable while the application update completes.';
  }
  if (code === 'workspace_scope_changed') {
    return 'The active workspace changed before this operation completed. Refresh and retry.';
  }
  return String(fallback || 'This operation could not be completed.');
}

export function safeFailureRecord(error, stage = 'runtime', retryable = true) {
  return `code=${safeDiagnosticCode(error)};stage=${boundedToken(stage)};retryable=${retryable === true ? 'true' : 'false'}`;
}
